import { useEffect, useRef, useState } from 'react'

// Click to edit. Enter or blur saves, Escape cancels. On successful save,
// the parent bumps its own ✓ indicator (via `onSaved`). On error, we revert
// to the original value and surface the error via `onError`.
export default function EditableCell({
  value,
  display, // optional custom renderer for the non-editing state
  type = 'text',
  placeholder,
  className = '',
  inputClassName = '',
  align = 'left',
  onCommit, // async (nextValue) — throws on failure
  onError,
  readOnly = false,
  formatOnSave, // e.g. Number for numeric fields
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!editing) setDraft(value ?? '')
  }, [value, editing])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select?.()
    }
  }, [editing])

  async function commit() {
    if (saving) return
    const normalized = formatOnSave ? formatOnSave(draft) : draft
    // Skip network call if unchanged.
    const before = formatOnSave ? formatOnSave(value ?? '') : value ?? ''
    if (String(normalized) === String(before)) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onCommit(normalized)
      setEditing(false)
    } catch (err) {
      setDraft(value ?? '')
      onError?.(err)
    } finally {
      setSaving(false)
    }
  }

  if (readOnly || !onCommit) {
    return <span className={className}>{display ?? value ?? '—'}</span>
  }

  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation()
          setEditing(true)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            setEditing(true)
          }
        }}
        className={`inline-block cursor-text ${className}`}
        style={{ minHeight: 20 }}
      >
        {display ?? (value === '' || value == null ? (
          <span className="text-ink-3">{placeholder || '—'}</span>
        ) : value)}
      </span>
    )
  }

  return (
    <input
      ref={inputRef}
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          setDraft(value ?? '')
          setEditing(false)
        }
      }}
      placeholder={placeholder}
      className={`w-full rounded px-1.5 py-1 text-inherit outline-none ${inputClassName}`}
      style={{
        border: '2px solid #1FC0D4',
        background: '#fff',
        textAlign: align,
        fontSize: 'inherit',
        fontWeight: 'inherit',
        color: 'inherit',
      }}
      disabled={saving}
    />
  )
}
